import { ResponsiveTableColumn } from 'modules/shared-components/data-display/table/types';
import { FC, useMemo } from 'react';
import ErrorBoundary from 'components/error-boundaries';
import Table from 'modules/shared-components/data-display/table';

import { twMerge } from 'tailwind-merge';

import { thDynamicClass } from './openTrades';
import {
  OpenPositionListItem,
  SnipePositionStatus,
} from 'api/types/httpsTypes/dex-trade';

import { useWebsocket } from 'api/hooks/useWebsocket';

interface Props {
  columnsDef: ResponsiveTableColumn<OpenPositionListItem>[];
  data: OpenPositionListItem;
  className?: string;
  isOpen?: boolean;
  isTerminal?: boolean;
  selectedTokens?: string[];
  setSelectedTokens?: React.Dispatch<React.SetStateAction<string[]>>;
  onOpen: () => void;
}

// Table row with websocket subscription
const SubscribedTableRow: FC<Props> = ({
  data,
  columnsDef,
  className,
  isOpen: isTradesOpen,
  isTerminal,
  selectedTokens,
  setSelectedTokens,
  onOpen,
}) => {
  const priceSub = useMemo(
    () => ({
      chain: data.chain,
      address: data.tokenAddress,
    }),
    [data.chain, data.tokenAddress]
  );

  // Always call the hook, no conditionals
  useWebsocket('token_price', priceSub);

  function openFunction() {
    if (selectedTokens && selectedTokens.length >= 1) {
      if (SnipePositionStatus[data.status]?.canSell && setSelectedTokens) {
        if (selectedTokens.includes(data.id)) {
          setSelectedTokens((prev) => prev.filter((t) => t !== data.id));
        } else {
          setSelectedTokens((prev) => [...prev, data.id]);
        }
      }
    } else {
      onOpen();
    }
  }

  return (
    <BaseTableRow
      isTerminal={isTerminal}
      data={data}
      columnsDef={columnsDef}
      className={className}
      isOpen={!!isTradesOpen}
      onOpen={openFunction}
    />
  );
};

// Table row without websocket subscription
const NonSubscribedTableRow: FC<Props> = ({
  data,
  columnsDef,
  className,
  isOpen: isTradesOpen,
  isTerminal,
  onOpen,
}) => {
  return (
    <BaseTableRow
      data={data}
      columnsDef={columnsDef}
      className={className}
      isOpen={!!isTradesOpen}
      isTerminal={isTerminal}
      onOpen={() => onOpen()}
    />
  );
};

// Shared base component to avoid duplication
const BaseTableRow: FC<{
  data: any;
  columnsDef: any[];
  className?: string;
  isOpen: boolean;
  onOpen: () => void;
  isTerminal?: boolean;
}> = ({
  data,
  columnsDef,
  className,
  isOpen: isTradesOpen,
  onOpen,
  isTerminal,
}) => {
  return (
    <tr
      onClick={onOpen}
      className={twMerge(
        isTerminal
          ? 'lg:grid-cols-[250px_1fr_1fr_1fr_1fr_1fr_100px]'
          : twMerge(
              isTradesOpen
                ? 'lg:grid-cols-[250px_1fr_1fr_1fr_1fr_1fr_1fr_1fr_1fr_100px]'
                : 'lg:grid-cols-[250px_1fr_1fr_1fr_1fr_1fr_100px]'
            ),
        'border-t border-dex-black-700  cursor-pointer items-center  lg:grid lg:dark:bg-dex-black-900 hover:dark:lg:bg-dex-black-800 ',
        className
      )}
    >
      <ErrorBoundary>
        {columnsDef.map((column) => {
          const Column = column.component;
          if (!Column) return;
          return (
            <Table.Td className={thDynamicClass(column.key)} key={column.key}>
              <Column data={data} />
            </Table.Td>
          );
        })}
      </ErrorBoundary>
    </tr>
  );
};

// For backwards compatibility
export const TableRow: FC<Props> = (props) => {
  const { isOpen } = props;
  return isOpen ? (
    <SubscribedTableRow {...props} />
  ) : (
    <NonSubscribedTableRow {...props} />
  );
};
